export const PRESERVED_FARM_COLLECTIONS = [
  "employees",
  "harasSkipReasons",
  "harasSettings",
  "animalSettings",
  "farmSettings",
];

export const FARM_COLLECTIONS = [
  "orders","animals","financial","checklists","vet","invoices","machinery",
  "machineryMaintenance","fueling","notifications","generationConflicts","pastures","poi","maps","services",
  "occurrences","inspections","animalLots","stockMovements","harasEquines","harasStalls",
  "harasSchedule",
  // v2.241.00: coleções operacionais que estavam fora do scheduler regular
  "stock","employees","harasNutrition","harasWeights",
  "animalServiceNutrition","animalFoalNutrition","lotIndividualizations","microchipAudit"
];

// v3.9.07: grupos sequenciais completos para pull_farm_bundle.
// Cada grupo vira uma request separada, em série, para ficar dentro do limite do HostGator.
export const FARM_BUNDLE_GROUPS = [
  // Grupo A — Operacional
  ["orders", "services", "occurrences", "notifications", "employees", "pastures", "poi"],
  // Grupo B — Animal/Vet
  ["animals", "animalLots", "vet", "checklists", "inspections", "harasEquines", "harasStalls", "harasSchedule"],
  // Grupo C — Maquinário/Estoque
  ["machinery", "machineryMaintenance", "fueling", "stock", "stockMovements", "maps"],
  // Grupo D — Financeiro/Auxiliar
  ["financial", "invoices", "generationConflicts", "harasNutrition", "harasWeights",
   "animalServiceNutrition", "animalFoalNutrition", "lotIndividualizations", "microchipAudit"],
];

const FARM_RESET_DEFAULTS = Object.freeze({
  orders: [],
  animals: [],
  financial: [],
  checklists: [],
  vet: [],
  invoices: [],
  machinery: [],
  machineryMaintenance: [],
  fueling: [],
  notifications: [],
  generationConflicts: [],
  pastures: [],
  poi: [],
  services: [],
  occurrences: [],
  inspections: [],
  animalLots: [],
  stockMovements: [],
  harasEquines: [],
  harasStalls: [],
  harasSchedule: [],
  stock: [],
  employees: [],
  harasNutrition: [],
  harasWeights: [],
  animalServiceNutrition: [],
  animalFoalNutrition: [],
  lotIndividualizations: [],
  microchipAudit: [],
  harasSkipReasons: [],
  harasSettings: [],
  animalSettings: [],
  farmSettings: [],
});

export function getFarmResetPayload(collection) {
  const value = FARM_RESET_DEFAULTS[collection];
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === "object") return { ...value };
  return [];
}

export function buildAdminDataApi(deps = {}) {
  const {
    DB,
    USER_DELETION_LOG_KEY,
    ensureDefaultAdminAccount,
    pruneDeletedAccounts,
    persistAdminCredentialState,
    pullGlobal,
    pushGlobal,
    createClientId,
  } = deps;

  async function getAccounts() {
    try {
      const local = await DB.get("auth:accounts");
      const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
      const pruned = pruneDeletedAccounts(local, deletionLog);
      const ensured = ensureDefaultAdminAccount(pruned, "get-accounts");
      const onlySeedAdmin = Array.isArray(ensured) && ensured.length === 1 && String(ensured?.[0]?.username || "").trim().toLowerCase() === "admin";
      if (Array.isArray(ensured) && ensured.length > 0 && (!navigator.onLine || !onlySeedAdmin)) {
        if (JSON.stringify(local || []) !== JSON.stringify(ensured)) {
          await DB.set("auth:accounts", ensured);
        }
        await persistAdminCredentialState(ensured);
        return ensured;
      }
      if (navigator.onLine) {
        try {
          const srv = await pullGlobal("auth:accounts", "admin-get-accounts-bootstrap");
          const remote = pruneDeletedAccounts(Array.isArray(srv?.data) ? srv.data : [], deletionLog);
          const hydrated = Array.isArray(remote) && remote.length > 0 ? ensureDefaultAdminAccount(remote, "admin-get-accounts-remote") : [];
          if (hydrated.length > 0) {
            await DB.set("auth:accounts", hydrated);
            if (srv?.version != null) await DB.set("auth:accounts:_ver", Number(srv.version || 0));
            await persistAdminCredentialState(hydrated);
            return hydrated;
          }
        } catch (_) {}
        return [];
      }
      const seed = ensureDefaultAdminAccount([], "get-accounts-seed");
      await DB.set("auth:accounts", seed);
      await persistAdminCredentialState(seed);
      return seed;
    } catch (e) {
      if (navigator.onLine) {
        try {
          const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
          const srv = await pullGlobal("auth:accounts", "admin-get-accounts-bootstrap-fallback");
          const remote = pruneDeletedAccounts(Array.isArray(srv?.data) ? srv.data : [], deletionLog);
          const hydrated = Array.isArray(remote) && remote.length > 0 ? ensureDefaultAdminAccount(remote, "admin-get-accounts-remote") : [];
          if (hydrated.length > 0) {
            await DB.set("auth:accounts", hydrated);
            if (srv?.version != null) await DB.set("auth:accounts:_ver", Number(srv.version || 0));
            await persistAdminCredentialState(hydrated);
            return hydrated;
          }
        } catch (_) {}
        return [];
      }
      const seed = ensureDefaultAdminAccount([], "get-accounts-seed");
      try { await DB.set("auth:accounts", seed); } catch (_) {}
      try { await persistAdminCredentialState(seed); } catch (_) {}
      return seed;
    }
  }

  async function saveAccounts(accounts) {
    const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
    const pruned = pruneDeletedAccounts(accounts, deletionLog);
    const ensured = ensureDefaultAdminAccount(pruned, "save-accounts");
    await DB.set("auth:accounts", ensured);
    await persistAdminCredentialState(ensured);
  }

  async function getUserDeletionLog(userName) {
    try {
      const local = await DB.get(USER_DELETION_LOG_KEY);
      const localArr = Array.isArray(local) ? local : [];
      try {
        const srv = await pullGlobal(USER_DELETION_LOG_KEY, userName);
        if (Array.isArray(srv?.data)) {
          const merged = [...srv.data, ...localArr]
            .filter(Boolean)
            .reduce((acc, item) => {
              const key = String(item?.id || "").trim() || JSON.stringify(item);
              if (!acc.map.has(key)) acc.map.set(key, item);
              else acc.map.set(key, { ...acc.map.get(key), ...item });
              return acc;
            }, { map: new Map() }).map;
          const out = [...merged.values()].sort((a, b) => String(b?.deletedAt || "").localeCompare(String(a?.deletedAt || "")));
          await DB.set(USER_DELETION_LOG_KEY, out);
          return out;
        }
      } catch (_) {}
      return localArr.sort((a, b) => String(b?.deletedAt || "").localeCompare(String(a?.deletedAt || "")));
    } catch (_) {
      return [];
    }
  }

  async function appendUserDeletionLog(entry, userName) {
    const record = {
      id: String(entry?.id || createClientId("udel")),
      deletedAt: entry?.deletedAt || new Date().toISOString(),
      deletedBy: entry?.deletedBy || userName || "sistema",
      ...entry,
    };
    const current = await getUserDeletionLog(userName);
    const next = [record, ...current.filter(item => String(item?.id || "") !== String(record.id || ""))]
      .sort((a, b) => String(b?.deletedAt || "").localeCompare(String(a?.deletedAt || "")));
    await DB.set(USER_DELETION_LOG_KEY, next);
    try {
      const res = await pushGlobal(USER_DELETION_LOG_KEY, next, userName);
      if (res?.version) await DB.set(`${USER_DELETION_LOG_KEY}:_ver`, res.version);
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: USER_DELETION_LOG_KEY } }));
    } catch (_) {}
    return next;
  }

  return {
    getAccounts,
    saveAccounts,
    getUserDeletionLog,
    appendUserDeletionLog,
    PRESERVED_FARM_COLLECTIONS,
    FARM_COLLECTIONS,
    getFarmResetPayload,
  };
}
